import { ReactNode, useId, useState } from 'react';

import Button from 'components/ui/button';
import { User } from 'types/user';
import { FOCO_VISIVEL } from 'utils/foco';

import { ATUACOES } from 'pages/RealizacaoDestaque/components/CartaoDeParticipacao';
import LinhaDoParticipante from './LinhaDoParticipante';
import { CAMPO, ROTULO } from './componentes-do-cadastro';
import { combinaComFiltro, situacaoDoParticipante, temDescricao } from './situacao-do-participante';

/** A partir de quantas pessoas aparece o campo "Filtrar participantes" (RCP-34). */
const MINIMO_PARA_BUSCA = 11;

interface ListaDeParticipantesProps {
  participantes: User[];
  percentuais: Record<string, unknown>;
  limites: { min: number; max: number };
  /** Liga o botão "Ver pendentes (p)", mostrado junto das mensagens que travam o avanço (RCP-35). */
  mostrarAtalhoDePendentes: boolean;
  idDoUsuario?: string;
  podeRemover: (pessoa: User) => boolean;
  onRemover: (pessoa: User, indice: number) => void;
  onEscolherPercentual: (chave: string, valor: number) => void;
  onAplicarEmMassa: (chaves: string[], valor: number) => void;
  editorDe: (pessoa: User, indice: number) => ReactNode;
  /** Observação por chave, mostrada na linha da pessoa (SUG-20). */
  dicas?: Record<string, string>;
}

type Filtro = 'todos' | 'pendentes';

function plural(quantidade: number, singular: string, plural: string) {
  return quantidade === 1 ? singular : plural;
}

/**
 * Participantes da realização com seleção, percentual em massa e filtros (RCP-23 a RCP-39). Pensada para listas de
 * 100 pessoas ou mais: cada linha é curta e o editor de descrição só abre quando pedido.
 */
export default function ListaDeParticipantes({
  participantes,
  percentuais,
  limites,
  mostrarAtalhoDePendentes,
  idDoUsuario,
  podeRemover,
  onRemover,
  onEscolherPercentual,
  onAplicarEmMassa,
  editorDe,
  dicas = {},
}: ListaDeParticipantesProps) {
  const buscaId = useId();
  const [filtro, setFiltro] = useState<Filtro>('todos');
  // Quem estava pendente quando o filtro foi aplicado: completar alguém não o tira da tela enquanto se digita (RCP-36).
  const [pendentesCongelados, setPendentesCongelados] = useState<Set<string>>(new Set());
  const [termo, setTermo] = useState('');
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [confirmacao, setConfirmacao] = useState<{ valor: number; descricoes: number }>();

  const situacaoDe = (pessoa: User) => situacaoDoParticipante(pessoa, percentuais[pessoa._id], limites);
  const pendentes = participantes.filter(pessoa => situacaoDe(pessoa) !== 'completo');
  const mostraBusca = participantes.length >= MINIMO_PARA_BUSCA;
  // Sem o campo na tela não há como apagar o termo, então ele deixa de filtrar.
  const termoAtivo = mostraBusca ? termo : '';
  const visiveis = participantes.filter(
    pessoa => (filtro === 'todos' || pendentesCongelados.has(pessoa._id)) && combinaComFiltro(pessoa, termoAtivo),
  );
  // Só conta quem ainda está na lista (RCP-38).
  const escolhidas = participantes.filter(pessoa => selecionados.has(pessoa._id));
  const todosVisiveisMarcados = visiveis.length > 0 && visiveis.every(pessoa => selecionados.has(pessoa._id));

  function mostrarPendentes() {
    setPendentesCongelados(new Set(pendentes.map(pessoa => pessoa._id)));
    setFiltro('pendentes');
  }

  function marcar(chaves: string[], marcado: boolean) {
    setSelecionados(atuais => {
      const novos = new Set(atuais);
      chaves.forEach(chave => (marcado ? novos.add(chave) : novos.delete(chave)));
      return novos;
    });
  }

  // Quem sai da lista sai da seleção e não volta marcado se for incluído de novo (RCP-38).
  function remover(pessoa: User, indice: number) {
    marcar([pessoa._id], false);
    onRemover(pessoa, indice);
  }

  function aplicar(valor: number) {
    onAplicarEmMassa(
      escolhidas.map(pessoa => pessoa._id),
      valor,
    );
    setSelecionados(new Set());
    setConfirmacao(undefined);
  }

  function pedirAplicacao(valor: number) {
    const descricoes = escolhidas.filter(temDescricao).length;
    if (descricoes > 0) setConfirmacao({ valor, descricoes });
    else aplicar(valor);
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <BotaoDeFiltro ativo={filtro === 'todos'} onClick={() => setFiltro('todos')}>
          Todos ({participantes.length})
        </BotaoDeFiltro>
        <BotaoDeFiltro ativo={filtro === 'pendentes'} onClick={mostrarPendentes}>
          Pendentes ({pendentes.length})
        </BotaoDeFiltro>
      </div>

      {mostraBusca && (
        <div>
          <label htmlFor={buscaId} className={ROTULO}>
            Filtrar participantes
          </label>
          <input
            id={buscaId}
            type="search"
            value={termo}
            onChange={evento => setTermo(evento.target.value)}
            placeholder="Nome ou matrícula"
            className={`${CAMPO} ${FOCO_VISIVEL}`}
          />
        </div>
      )}

      <label className="flex items-center gap-2 text-xs font-bold text-content">
        <input
          type="checkbox"
          checked={todosVisiveisMarcados}
          onChange={evento =>
            marcar(
              visiveis.map(pessoa => pessoa._id),
              evento.target.checked,
            )
          }
          className={`h-4 w-4 accent-brand-blue ${FOCO_VISIVEL}`}
        />
        Selecionar todos ({visiveis.length})
      </label>

      {escolhidas.length > 0 && (
        <div className="flex flex-col gap-2 rounded-jj-md border border-brand-blue bg-brand-white px-3.5 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <p className="m-0 text-xs font-bold text-brand-blue-dark">
              Aplicar a {escolhidas.length} {plural(escolhidas.length, 'selecionado', 'selecionados')}
            </p>
            {ATUACOES.map(atuacao => (
              <button
                key={atuacao.valor}
                type="button"
                aria-label={`Aplicar ${atuacao.valor}% aos selecionados`}
                onClick={() => pedirAplicacao(atuacao.valor)}
                className={`rounded-jj-md border-2 border-surface-200 bg-surface-100 px-2.5 py-1 font-display text-xs font-extrabold text-content hover:border-brand-blue ${FOCO_VISIVEL}`}
              >
                {atuacao.valor}%
              </button>
            ))}
          </div>

          {confirmacao && (
            <div role="alert" className="flex flex-wrap items-center gap-2.5 text-xs text-brand-blue-dark">
              <p className="m-0">
                A descrição de {confirmacao.descricoes}{' '}
                {plural(confirmacao.descricoes, 'participante', 'participantes')} será apagada.
              </p>
              <Button variant="solido-destaque" size="sm" onClick={() => aplicar(confirmacao.valor)}>
                Aplicar mesmo assim
              </Button>
              <Button variant="texto" size="sm" onClick={() => setConfirmacao(undefined)}>
                Cancelar
              </Button>
            </div>
          )}
        </div>
      )}

      {visiveis.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {visiveis.map(pessoa => {
            const indice = participantes.indexOf(pessoa);

            return (
              <LinhaDoParticipante
                key={pessoa._id}
                pessoa={pessoa}
                percentual={percentuais[pessoa._id]}
                situacao={situacaoDe(pessoa)}
                selecionada={selecionados.has(pessoa._id)}
                onSelecionar={marcado => marcar([pessoa._id], marcado)}
                onEscolherPercentual={valor => onEscolherPercentual(pessoa._id, valor)}
                onRemover={podeRemover(pessoa) ? () => remover(pessoa, indice) : undefined}
                ehVoce={pessoa._id === idDoUsuario}
                dica={dicas[pessoa._id]}
                editor={() => editorDe(pessoa, indice)}
              />
            );
          })}
        </ul>
      ) : (
        participantes.length > 0 && <p className="m-0 text-xs text-content">Nenhum participante encontrado para esse filtro.</p>
      )}

      {mostrarAtalhoDePendentes && pendentes.length > 0 && (
        <Button variant="secundario" size="sm" className="self-start" onClick={mostrarPendentes}>
          Ver pendentes ({pendentes.length})
        </Button>
      )}
    </div>
  );
}

function BotaoDeFiltro({ ativo, onClick, children }: { ativo: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={onClick}
      className={`rounded-jj-pill border px-3 py-1 text-xs font-bold ${
        ativo ? 'border-brand-blue bg-brand-blue text-brand-white' : 'border-content-soft bg-brand-white text-content'
      } ${FOCO_VISIVEL}`}
    >
      {children}
    </button>
  );
}
